import React from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ROLE_LABELS, ROLES } from "@/lib/web3";
import { Shield, Coins, Pause, ShieldOff, Flame, ArrowUpToLine, Crown } from "lucide-react";

const ROLE_DESCRIPTIONS = [
  { role: ROLES.DEFAULT_ADMIN_ROLE, icon: Crown, desc: "Grants & revokes every other role. Sets max supply. Recovers stuck ERC-20s." },
  { role: ROLES.UPGRADER_ROLE, icon: ArrowUpToLine, desc: "Authorizes UUPS upgrades. Kept separate from Owner so the upgrade key can be its own multi-sig." },
  { role: ROLES.MASTER_MINTER_ROLE, icon: Shield, desc: "Adds/removes Minter holders and sets each minter's mint allowance." },
  { role: ROLES.MINTER_ROLE, icon: Coins, desc: "Mints PUSD up to its remaining per-minter allowance. Cannot mint while paused." },
  { role: ROLES.PAUSER_ROLE, icon: Pause, desc: "Pauses / unpauses all transfers (including mint and burn)." },
  { role: ROLES.BLACKLISTER_ROLE, icon: ShieldOff, desc: "Freezes / unfreezes addresses. Frozen addresses cannot send, receive, or be minted to." },
  { role: ROLES.BURNER_ROLE, icon: Flame, desc: "Burns own balance, or pulled balance via burnFrom, when reserves are redeemed." },
];

const FEATURES = [
  ["ERC-20 + BEP-20 compatible", "Standard transfer / approval interface, 6 decimals (USDC-aligned)."],
  ["UUPS upgradeable proxy", "Implementation can be replaced without changing the token address."],
  ["EIP-2612 Permit", "Gasless approvals via signature."],
  ["Mint / Burn", "Per-minter allowance with cap on max supply."],
  ["Blacklist", "Frozen addresses cannot transact in any direction."],
  ["Pause", "Global circuit breaker for incident response."],
  ["Recover lost tokens", "Owner can rescue non-PUSD ERC-20s sent by mistake."],
  ["Max supply cap", "Configurable hard ceiling. Set 0 for uncapped."],
  ["Multi-sig friendly", "Any role can be a Gnosis Safe address."],
  ["Compliance events", "Dedicated events for Mint, Burn, Blacklist, Role changes, Pause, Recover."],
  ["DAO-ready", "Roles are addresses — point them at a Timelock/Governor at any time."],
];

const SAMPLE = `// SPDX-License-Identifier: MIT
// PUSD — Production-grade stablecoin on the PIO blockchain
// See /app/contracts/contracts/PUSD.sol for full source.

contract PUSD is
    Initializable,
    ERC20Upgradeable,
    ERC20PermitUpgradeable,
    ERC20PausableUpgradeable,
    AccessControlUpgradeable,
    UUPSUpgradeable
{
    bytes32 public constant MASTER_MINTER_ROLE = keccak256("MASTER_MINTER_ROLE");
    bytes32 public constant MINTER_ROLE        = keccak256("MINTER_ROLE");
    bytes32 public constant PAUSER_ROLE        = keccak256("PAUSER_ROLE");
    bytes32 public constant BLACKLISTER_ROLE   = keccak256("BLACKLISTER_ROLE");
    bytes32 public constant BURNER_ROLE        = keccak256("BURNER_ROLE");
    bytes32 public constant UPGRADER_ROLE      = keccak256("UPGRADER_ROLE");

    uint256 public maxSupply;
    mapping(address => uint256) public minterAllowance;
    mapping(address => bool)    public isBlacklisted;
    /* … see full file for body … */
}`;

export default function ContractDocs() {
  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Contract architecture</div>
        <h1 className="text-3xl font-semibold tracking-tight">PUSD — USDC-style stablecoin on PIO</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">
          A production-grade ERC-20 stablecoin built with OpenZeppelin's audited upgradeable libraries. The full Hardhat project (contracts, deployment scripts, tests, PIO network config) lives in <code className="font-mono text-xs px-1.5 py-0.5 bg-slate-100 rounded">/app/contracts</code>.
        </p>
      </div>

      <Card className="border-slate-200 shadow-none rounded-md">
        <CardHeader className="border-b border-slate-200 py-3">
          <CardTitle className="text-sm font-semibold tracking-tight">Roles</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y divide-slate-200">
            {ROLE_DESCRIPTIONS.map(({ role, icon: Icon, desc }) => (
              <li key={role} className="px-5 py-4 flex items-start gap-4">
                <div className="w-9 h-9 rounded-sm bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold tracking-tight">{ROLE_LABELS[role]}</span>
                    <code className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{role.slice(0, 10)}…</code>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none rounded-md">
        <CardHeader className="border-b border-slate-200 py-3">
          <CardTitle className="text-sm font-semibold tracking-tight">Features</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 p-5">
          {FEATURES.map(([title, desc]) => (
            <div key={title} className="flex items-start gap-3">
              <Badge variant="outline" className="font-mono text-[10px] mt-0.5 shrink-0 border-emerald-200 text-emerald-700 bg-emerald-50">✓</Badge>
              <div>
                <div className="text-sm font-semibold tracking-tight">{title}</div>
                <div className="text-xs text-slate-500">{desc}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none rounded-md">
        <CardHeader className="border-b border-slate-200 py-3">
          <CardTitle className="text-sm font-semibold tracking-tight">Source preview</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <pre className="font-mono text-[12px] leading-relaxed p-5 overflow-auto bg-slate-900 text-slate-100 rounded-b-md">
{SAMPLE}
          </pre>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none rounded-md">
        <CardHeader className="border-b border-slate-200 py-3">
          <CardTitle className="text-sm font-semibold tracking-tight">Deploying to PIO</CardTitle>
        </CardHeader>
        <CardContent className="p-5 text-sm space-y-3 text-slate-700">
          <ol className="list-decimal pl-5 space-y-2">
            <li><code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">cd /app/contracts</code> then <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">yarn compile</code> and <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">yarn test</code>. All 10 tests should pass.</li>
            <li>Copy <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">.env.example</code> to <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">.env</code> and fill in <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">PIO_RPC_URL</code>, <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">PIO_CHAIN_ID</code>, role multi-sigs, etc.</li>
            <li><code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">yarn deploy:pio-testnet</code> first, then <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">yarn deploy:pio</code> for mainnet.</li>
            <li>Paste the deployed proxy address into the Settings page → Save. The console switches from DEMO to LIVE.</li>
            <li>Configure a minter: <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">yarn hardhat run scripts/configure.js --network pio</code> or use the Operations tab.</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
